import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Search, X } from "lucide-react";

const CATS = ["Banarasi", "Kanjivaram", "Silk", "Chiffon", "Georgette", "Cotton"];
const PRICE_RANGES = [
  { label: "Under ₹3,000", min: 0, max: 3000 },
  { label: "₹3,000 – ₹7,000", min: 3000, max: 7000 },
  { label: "₹7,000 – ₹15,000", min: 7000, max: 15000 },
  { label: "Above ₹15,000", min: 15000, max: 1000000 },
];
const COLORS = [
  { name: "Red", swatch: "#7a1e2b" },
  { name: "Pink", swatch: "#e8a5b0" },
  { name: "Blue", swatch: "#2f4a6b" },
  { name: "Green", swatch: "#2f5a3a" },
  { name: "Yellow", swatch: "#d9b23a" },
  { name: "Black", swatch: "#111" },
  { name: "Ivory", swatch: "#f2ead4" },
  { name: "Gold", swatch: "#c9a34a" },
];
const SIZES = ["Free Size", "XS", "S", "M", "L", "XL"];

// Debounce hook
function useDebounce(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qLocal, setQLocal] = useState(params.get("q") || "");
  const q = useDebounce(qLocal, 250);

  const category = params.get("category") || "";
  const isNew = params.get("new") === "1";
  const priceIdx = parseInt(params.get("price") || "-1", 10);
  const color = params.get("color") || "";
  const size = params.get("size") || "";
  const sort = params.get("sort") || "created_at";
  const priceRange = priceIdx >= 0 ? PRICE_RANGES[priceIdx] : null;

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (category) qs.set("category", category);
    if (isNew) qs.set("new_arrival", "true");
    if (priceRange) { qs.set("min_price", priceRange.min); qs.set("max_price", priceRange.max); }
    if (color) qs.set("color", color);
    if (size) qs.set("size", size);
    if (q) qs.set("q", q);
    if (sort) qs.set("sort", sort);
    api.get(`/products?${qs.toString()}`).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [category, isNew, priceIdx, color, size, sort, q]);

  const updateParam = (key, value) => {
    const p = new URLSearchParams(params);
    if (value === null || value === "" || value === false) p.delete(key);
    else p.set(key, value);
    setParams(p);
  };

  const activeChips = useMemo(() => {
    const chips = [];
    if (category) chips.push({ key: "category", label: category, remove: () => updateParam("category", "") });
    if (isNew) chips.push({ key: "new", label: "New Arrivals", remove: () => updateParam("new", null) });
    if (priceRange) chips.push({ key: "price", label: priceRange.label, remove: () => updateParam("price", null) });
    if (color) chips.push({ key: "color", label: color, remove: () => updateParam("color", "") });
    if (size) chips.push({ key: "size", label: size, remove: () => updateParam("size", "") });
    if (q) chips.push({ key: "q", label: `"${q}"`, remove: () => { setQLocal(""); updateParam("q", ""); } });
    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, isNew, priceIdx, color, size, q]);

  const heading = useMemo(() => {
    if (q) return `Results for "${q}"`;
    if (category) return category + " Sarees";
    if (isNew) return "New Arrivals";
    return "The Full Collection";
  }, [category, isNew, q]);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid="shop-page">
      <div className="mb-10">
        <div className="text-[11px] tracking-[0.3em] uppercase text-[#c9a34a] mb-3">Shop</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#2b1b1c]">{heading}</h1>
        <p className="mt-3 text-[#7c7273] max-w-2xl">Hand-selected weaves from the finest looms of India.</p>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-10">
        <aside className="lg:sticky lg:top-28 lg:self-start space-y-8" data-testid="shop-sidebar">
          <div>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7c7273]" />
              <Input
                placeholder="Search sarees..."
                value={qLocal}
                onChange={(e) => setQLocal(e.target.value)}
                className="pl-9 bg-white border-[#e8e4db] rounded-none"
                data-testid="shop-search-input"
              />
              {qLocal && (
                <button
                  onClick={() => setQLocal("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#7c7273] hover:text-[#7a1e2b]"
                  data-testid="shop-search-clear"
                  aria-label="clear"
                ><X size={14} /></button>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-[11px] tracking-[0.3em] uppercase text-[#7a1e2b] mb-3">Category</h3>
            <ul className="space-y-2">
              <li>
                <button
                  className={`text-sm ${!category ? "text-[#7a1e2b] font-medium" : "text-[#2b1b1c]"}`}
                  onClick={() => updateParam("category", "")}
                  data-testid="filter-cat-all"
                >All</button>
              </li>
              {CATS.map((c) => (
                <li key={c}>
                  <button
                    className={`text-sm ${category === c ? "text-[#7a1e2b] font-medium" : "text-[#2b1b1c]"}`}
                    onClick={() => updateParam("category", c)}
                    data-testid={`filter-cat-${c}`}
                  >{c}</button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[11px] tracking-[0.3em] uppercase text-[#7a1e2b] mb-3">Price</h3>
            <ul className="space-y-2">
              {PRICE_RANGES.map((r, i) => (
                <li key={r.label} className="flex items-center gap-2">
                  <Checkbox
                    id={`price-${i}`}
                    checked={priceIdx === i}
                    onCheckedChange={(v) => updateParam("price", v ? i : null)}
                    data-testid={`filter-price-${i}`}
                  />
                  <label htmlFor={`price-${i}`} className="text-sm text-[#2b1b1c] cursor-pointer">{r.label}</label>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[11px] tracking-[0.3em] uppercase text-[#7a1e2b] mb-3">Color</h3>
            <div className="flex flex-wrap gap-2">
              {COLORS.map((c) => (
                <button
                  key={c.name}
                  onClick={() => updateParam("color", color === c.name ? "" : c.name)}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${color === c.name ? "border-[#7a1e2b] scale-110" : "border-[#e8e4db]"}`}
                  style={{ background: c.swatch }}
                  title={c.name}
                  aria-label={c.name}
                  data-testid={`filter-color-${c.name}`}
                />
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-[11px] tracking-[0.3em] uppercase text-[#7a1e2b] mb-3">Blouse Size</h3>
            <div className="flex flex-wrap gap-2">
              {SIZES.map((s) => (
                <button
                  key={s}
                  onClick={() => updateParam("size", size === s ? "" : s)}
                  className={`min-w-[42px] px-3 py-1.5 text-xs border transition-colors ${size === s ? "bg-[#7a1e2b] text-white border-[#7a1e2b]" : "border-[#e8e4db] text-[#2b1b1c] hover:border-[#7a1e2b]"}`}
                  data-testid={`filter-size-${s}`}
                >{s}</button>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-[11px] tracking-[0.3em] uppercase text-[#7a1e2b] mb-3">More</h3>
            <div className="flex items-center gap-2">
              <Checkbox
                id="new"
                checked={isNew}
                onCheckedChange={(v) => updateParam("new", v ? "1" : null)}
                data-testid="filter-new"
              />
              <label htmlFor="new" className="text-sm text-[#2b1b1c] cursor-pointer">New Arrivals</label>
            </div>
          </div>
        </aside>

        <div>
          <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
            <div className="text-sm text-[#7c7273]">{products.length} pieces</div>
            <Select value={sort} onValueChange={(v) => updateParam("sort", v)}>
              <SelectTrigger className="w-[200px] bg-white border-[#e8e4db] rounded-none" data-testid="sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at">Newest first</SelectItem>
                <SelectItem value="price_asc">Price: Low to high</SelectItem>
                <SelectItem value="price_desc">Price: High to low</SelectItem>
                <SelectItem value="rating">Highest rated</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {activeChips.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6" data-testid="active-chips">
              {activeChips.map((c) => (
                <button
                  key={c.key}
                  onClick={c.remove}
                  className="text-xs px-3 py-1 border border-[#c9a34a] text-[#7a1e2b] bg-white flex items-center gap-1 hover:bg-[#7a1e2b] hover:text-white transition-colors"
                >
                  {c.label} <X size={10} />
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="py-24 text-center text-[#7c7273]">Loading beautiful weaves…</div>
          ) : products.length === 0 ? (
            <div className="py-24 text-center">
              <p className="font-italic-serif text-xl text-[#7c7273]">No sarees match these filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
              {products.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
